import React, { useMemo, useState } from 'react';

interface DbScriptViewerProps {
  script: string;
}

// Finds multi-line foldable regions: bracket pairs ( ) [ ] { } and SQL
// BEGIN...END blocks. Only the outermost region starting on a given line
// is kept, so each line shows at most one fold toggle.
function computeFoldRegions(lines: string[]): Map<number, number> {
  const regions: { start: number; end: number }[] = [];
  const openChars = '([{';
  const closeChars = ')]}';
  const matchOpen: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

  const bracketStack: { char: string; line: number }[] = [];
  lines.forEach((line, idx) => {
    for (const ch of line) {
      if (openChars.includes(ch)) {
        bracketStack.push({ char: ch, line: idx });
      } else if (closeChars.includes(ch)) {
        const top = bracketStack.pop();
        if (top && top.char === matchOpen[ch] && top.line !== idx) {
          regions.push({ start: top.line, end: idx });
        }
      }
    }
  });

  const beginStack: number[] = [];
  lines.forEach((line, idx) => {
    const hasBegin = /\bBEGIN\b/i.test(line);
    const hasEnd = /\bEND\b/i.test(line);
    if (hasBegin && !hasEnd) {
      beginStack.push(idx);
    } else if (hasEnd) {
      const start = beginStack.pop();
      if (start !== undefined && start !== idx) {
        regions.push({ start, end: idx });
      }
    }
  });

  const byStart = new Map<number, number>();
  regions.forEach(({ start, end }) => {
    const existing = byStart.get(start);
    if (existing === undefined || end > existing) {
      byStart.set(start, end);
    }
  });
  return byStart;
}

const DbScriptViewer: React.FC<DbScriptViewerProps> = ({ script }) => {
  const lines = useMemo(() => script.split('\n'), [script]);
  const foldRegions = useMemo(() => computeFoldRegions(lines), [lines]);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const toggleFold = (start: number) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(start)) next.delete(start);
      else next.add(start);
      return next;
    });
  };

  const isHidden = (idx: number): boolean => {
    for (const [start, end] of foldRegions) {
      if (collapsed.has(start) && idx > start && idx <= end) return true;
    }
    return false;
  };

  const hasFolds = foldRegions.size > 0;

  return (
    <div className="rounded-lg overflow-hidden border border-gray-700">
      {hasFolds && (
        <div className="flex items-center gap-2 bg-gray-800 px-3 py-1.5 border-b border-gray-700">
          <button
            onClick={() => setCollapsed(new Set(foldRegions.keys()))}
            className="text-xs text-gray-300 hover:text-white px-2 py-0.5 rounded hover:bg-gray-700 transition-colors"
          >
            Collapse All
          </button>
          <button
            onClick={() => setCollapsed(new Set())}
            className="text-xs text-gray-300 hover:text-white px-2 py-0.5 rounded hover:bg-gray-700 transition-colors"
          >
            Expand All
          </button>
        </div>
      )}
      <div className="bg-gray-900 text-green-400 p-2 overflow-auto text-sm font-mono max-h-[60vh]">
        {lines.length === 0 || (lines.length === 1 && lines[0] === '') ? (
          <div className="text-gray-500 italic px-2">No script content available</div>
        ) : (
          lines.map((line, idx) => {
            if (isHidden(idx)) return null;
            const foldEnd = foldRegions.get(idx);
            const isFoldable = foldEnd !== undefined;
            const isCollapsedHere = isFoldable && collapsed.has(idx);
            return (
              <div key={idx} className="flex hover:bg-gray-800/60 px-2 rounded">
                <span className="select-none text-gray-500 w-10 text-right pr-3 shrink-0">{idx + 1}</span>
                <span className="w-4 shrink-0 text-gray-400">
                  {isFoldable && (
                    <button
                      onClick={() => toggleFold(idx)}
                      className="hover:text-white leading-none"
                      title={isCollapsedHere ? 'Expand' : 'Collapse'}
                    >
                      {isCollapsedHere ? '▶' : '▼'}
                    </button>
                  )}
                </span>
                <span className="whitespace-pre break-words flex-1">
                  {line || ' '}
                  {isCollapsedHere && (
                    <span className="text-gray-500 italic"> … ({foldEnd - idx} lines hidden)</span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DbScriptViewer;
